import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site-data";
import { cn } from "@/lib/utils";

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-border/70"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      )}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-brand"
          className="flex flex-col leading-none group"
        >
          <span className="font-serif text-2xl text-navy tracking-tight">
            {SITE.name}
          </span>
          <span className="eyebrow mt-1">{SITE.title}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "text-[13px] tracking-wide uppercase font-medium link-underline",
                  isActive ? "text-navy" : "text-muted-foreground hover:text-navy"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-consult"
            className="inline-flex items-center gap-2 bg-navy text-white px-5 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#132644] transition-colors"
          >
            Book Consultation
            <span className="h-px w-6 bg-gold" />
          </Link>
        </div>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-navy"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-border bg-white"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "text-base py-2 border-b border-border last:border-b-0",
                    isActive ? "text-navy font-semibold" : "text-muted-foreground"
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex justify-center bg-navy text-white px-5 py-3 text-xs uppercase tracking-[0.22em] font-semibold"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
